import { type GetTicketCommentsResponse } from "@mg/schemas/src/christo/catalyst";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type TLShapeId, type Editor } from "@tldraw/tldraw";
import { useEffect } from "react";

import { askPunttProjectKeys } from "./askPunttProjects";

import {
  visibleAiCommentCount,
  visibleHumanCommentCount,
} from "../../routes/ask-puntt/utils/visibleComments";
import { TicketComment } from "../../routes/tickets/components/CommentTool/CommentPinUtil";
import { type GetProjectReviewsResponse } from "../../services/askPunttProjectFeed.types";
import { updateGuidelines } from "../../services/brand-guidelines";
import {
  addReviewersToTicket,
  createComment,
  createProject,
  createRevision,
  deleteCommentMessage,
  editComment,
  fetchTicketComments,
  generateBrief,
  generateMoodBoard,
  getBriefTemplates,
  getProject,
  getProjects,
  reviewTicket,
  updateProject,
  updateRevision,
  sendToDesign,
  getPromptFromTitle,
  fetchDownloadableRevisionAsset,
  fetchRecommendedCreatives,
  fetchRecommendedReviewers,
  approveTicket,
  validateBrief,
  shareTicket,
  deleteTicket,
  createFolder,
  getFoldersAndTickets,
  deleteFolder,
  updateFolder,
  shareTicket2,
  requestAskPunttComment,
  getDownloadTicketLink,
  combineTickets,
  deleteRevision,
  getTicketVersionStatus,
  generateIntegrationSummary,
  submitIntegrationSummary,
} from "../../services/projects";
import { isFetchNetworkError } from "../fetchNetworkError";
import { isNil } from "../fp";
import { useAppDispatch, useAppSelector } from "../hooks";
import {
  mergePunttProjectState,
  removeTicket,
  updateBreadcrumbName,
} from "../slices/punttProjects";
import {
  setComments,
  setRevisions,
  setTicket,
  updateCommentById,
} from "../slices/ticket";

export function useProjects() {
  const query = useQuery({
    queryKey: ["projects"],
    queryFn: getProjects,
  });

  return query;
}

export function useProjectMutation() {
  const mutation = useMutation({
    mutationKey: ["create-project"],
    mutationFn: createProject,
  });

  return mutation;
}

export function useProject(ticketId: string) {
  const dispatch = useAppDispatch();

  const query = useQuery({
    queryKey: ["tickets", ticketId],
    queryFn: () => getProject(ticketId),
  });

  useEffect(() => {
    if (query.data != null) {
      dispatch(setTicket(query.data));
    }
  }, [dispatch, query.data]);

  return query;
}

export function useGetTicketMutation() {
  return useMutation({
    mutationKey: ["ticket"],
    mutationFn: (ticketId: string) => getProject(ticketId),
  });
}

export function useEditProjectMutation(ticketId?: string) {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationKey: ["ticket", ticketId],
    mutationFn: updateProject,
    onSuccess(data) {
      // Update the Redux store with the new ticket data
      if (data) {
        dispatch(setTicket(data));
      }

      // Invalidate relevant queries to trigger re-fetches
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      if (ticketId) {
        queryClient.invalidateQueries({ queryKey: ["ticket", ticketId] });
      }
    },
  });

  return mutation;
}

export function useBriefTemplates() {
  const query = useQuery({
    queryKey: ["brief-templates"],
    queryFn: getBriefTemplates,
  });

  return query;
}

export function useGenerateBriefMutation() {
  const mutation = useMutation({
    mutationKey: ["ai-brief-generator"],
    mutationFn: generateBrief,
  });

  return mutation;
}

export function useValidateBriefMutation() {
  const mutation = useMutation({
    mutationKey: ["validate-brief"],
    mutationFn: validateBrief,
  });

  return mutation;
}

export function useGenerateMoodBoardMutation() {
  const mutation = useMutation({
    mutationKey: ["mood-board-generator"],
    mutationFn: generateMoodBoard,
  });

  return mutation;
}

export function useNewCommentMutation() {
  const dispatch = useAppDispatch();
  const comments = useAppSelector((state) => state.ticket.comments);
  const mutation = useMutation({
    mutationKey: ["new-comment"],
    mutationFn: createComment,
  });

  useEffect(() => {
    if (mutation.data != null) {
      const commentsCopy = comments.map((comment) => {
        return comment._id === mutation.data._id ? mutation.data : comment;
      });

      dispatch(setComments(commentsCopy));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, mutation.data]);

  return mutation;
}

export function useGetTicketComments(params: {
  ticketId: string;
  query: string;
}) {
  const dispatch = useAppDispatch();

  const query = useQuery({
    queryKey: ["ticket-comments", params.ticketId, params.query],
    queryFn: () => fetchTicketComments(params),
  });

  useEffect(() => {
    if (query.data != null) {
      dispatch(setComments(query.data));
    }
  }, [dispatch, query.data]);

  return query;
}

export function useEditCommentMutation() {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: ["edit-comment"],
    mutationFn: editComment,
    onSuccess: (data, variables) => {
      dispatch(updateCommentById(data));
      const commentQueryKey = [
        "ticket-view-comments",
        variables.ticketId,
      ] as const;
      const nextComments = queryClient.setQueryData<GetTicketCommentsResponse>(
        commentQueryKey,
        (old) => {
          if (!old) return old;
          return old.map((comment) =>
            comment._id === data._id ? data : comment,
          );
        },
      );
      // Only recompute the projects-list review counts when the
      // ticket-view-comments cache actually exists. When it is not loaded,
      // setQueryData returns undefined; falling back to [] here would overwrite
      // the list's real aiCommentCount/humanCommentCount with 0/0.
      if (nextComments == null) return;
      const aiCommentCount = visibleAiCommentCount(nextComments);
      const humanCommentCount = visibleHumanCommentCount(nextComments);
      queryClient.setQueriesData<GetProjectReviewsResponse>(
        { queryKey: [...askPunttProjectKeys.all, "reviews"] },
        (old) => {
          if (!old?.reviews?.length) return old;
          let changed = false;
          const reviews = old.reviews.map((review) => {
            if (review.ticketId !== variables.ticketId) return review;
            changed = true;
            return { ...review, aiCommentCount, humanCommentCount };
          });
          return changed ? { ...old, reviews } : old;
        },
      );
    },
  });

  return mutation;
}

export function useUpdateGuidelinesMutation() {
  const mutation = useMutation({
    mutationKey: ["update-brief"],
    mutationFn: updateGuidelines,
  });

  return mutation;
}

export function useDeleteCommentMutation({
  parentCommentId,
  editor,
}: {
  parentCommentId: string;
  editor: Editor | null | undefined;
}) {
  const dispatch = useAppDispatch();
  const comments = useAppSelector((state) => state.ticket.comments);
  const mutation = useMutation({
    mutationKey: ["delete-project"],
    mutationFn: deleteCommentMessage,
  });
  function getShapeFromComment(commentId: string) {
    if (editor == null) return null;
    const shapes = editor.getCurrentPageShapes();
    const thisShape = shapes.find((shape) => {
      if (shape.type !== "comment") {
        return null;
      }

      let meta: { _id?: unknown };
      try {
        meta = JSON.parse(
          (shape.meta.comment as string) ?? "{}",
        ) as TicketComment;
      } catch {
        return null;
      }

      if (meta._id === commentId) {
        return shape;
      }

      return null;
    });

    return thisShape;
  }
  useEffect(() => {
    if (mutation.status === "success") {
      const replaceCommentIndex = comments.findIndex(
        (comment) => comment._id === parentCommentId,
      );
      // why: a stale slice (comment already removed by an earlier update, or a
      // race) leaves parentCommentId absent, so findIndex returns -1.
      // commentsCopy[-1] is undefined and the .messages.slice() below throws,
      // and splice(-1, 1) would silently drop the LAST comment instead. Bail.
      if (replaceCommentIndex === -1) return;
      const shape = getShapeFromComment(parentCommentId);

      // we deleted a parent comment, then we need to remove
      // the entire comment from the slice.
      if (parentCommentId === mutation.variables.messageId) {
        if (shape) {
          editor?.setSelectedShapes([shape.id]);
          editor?.deleteShapes([
            shape.id,
            shape.meta?.linkedAvatarId as TLShapeId,
          ]);
        }
        const commentsCopy = [...comments];
        commentsCopy.splice(replaceCommentIndex, 1);

        dispatch(setComments(commentsCopy));
        return;
      }

      // otherwise, we deleted a message and we need to just remove
      // that one message from the list. This is a pain in the ass
      // because reads from slices are read-only, so we have to
      // stringify-parse method in order to mutate a deep array.
      const commentsCopy = JSON.parse(
        JSON.stringify(comments),
      ) as typeof comments;
      const parentComment = commentsCopy[replaceCommentIndex];
      const repliesCopy = parentComment.messages.slice();
      const replaceMessageIndex = repliesCopy.findIndex(
        (message) => message._id === mutation.variables.messageId,
      );
      // When the reply is not in the local slice (stale comments, already
      // removed by an earlier update, or a race), findIndex returns -1.
      // splice(-1, 1) would delete the LAST reply in the thread — silently
      // dropping an unrelated message. Bail out instead of corrupting the list.
      if (replaceMessageIndex === -1) return;
      repliesCopy.splice(replaceMessageIndex, 1);
      parentComment.messages = repliesCopy;

      if (shape != null) {
        editor?.bringToFront([shape.id]);
        editor?.updateShape({
          ...shape,
          meta: {
            ...shape.meta,
            comment: JSON.stringify(parentComment),
          },
          props: {
            ...shape.props,
            commentId: parentCommentId,
          },
        });
        editor?.setSelectedShapes([shape.id]);
      }

      commentsCopy.splice(replaceCommentIndex, 1, parentComment);

      dispatch(setComments(commentsCopy));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, mutation.status, mutation.variables, parentCommentId]);

  return mutation;
}

export function useRevisionMutation() {
  const mutation = useMutation({
    mutationKey: ["create-revision"],
    mutationFn: createRevision,
  });

  return mutation;
}

export { isFetchNetworkError };

export function useUpdateRevisionMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: ["update-revision"],
    mutationFn: updateRevision,
    // why: the canvas (AssetCanvas) retries a save the network dropped and
    // warns once. Logged here, that blip became a RUM error that paged
    // #eng-errors (monitor 116354465). A mutation-level onError replaces the
    // client default, so every other failure is handed back to it.
    onError(...args) {
      if (isFetchNetworkError(args[0])) return;
      return queryClient.getDefaultOptions().mutations?.onError?.(...args);
    },
  });

  return mutation;
}

export function useDeleteRevisionMutation(ticketId: string) {
  const dispatch = useAppDispatch();

  return useMutation({
    mutationKey: ["delete-revision"],
    mutationFn: (revisionId: string) => deleteRevision(ticketId, revisionId),
    onSuccess(data) {
      if (data == null) {
        return;
      }

      const { boards } = data;

      dispatch(setRevisions(boards));
    },
  });
}

export function useAddReviewersMutation() {
  const mutation = useMutation({
    mutationKey: ["add-reviewers"],
    mutationFn: addReviewersToTicket,
  });

  return mutation;
}

export function useApproveTicketMutation(ticketId: string) {
  const mutation = useMutation({
    mutationKey: ["approve-ticket", ticketId],
    mutationFn: approveTicket,
  });

  return mutation;
}

export function useReviewRevisionMutation() {
  const mutation = useMutation({
    mutationKey: ["review-revision"],
    mutationFn: reviewTicket,
  });

  return mutation;
}

export function useSendToDesignMutation() {
  const mutation = useMutation({
    mutationKey: ["send-to-design"],
    mutationFn: sendToDesign,
  });

  return mutation;
}

export function usePromptFromTitleMutation() {
  const mutation = useMutation({
    mutationKey: ["prompt-title"],
    mutationFn: getPromptFromTitle,
  });

  return mutation;
}

export function useGetDownloadableAsset() {
  const mutation = useMutation({
    mutationKey: ["revision-asset"],
    mutationFn: fetchDownloadableRevisionAsset,
  });

  return mutation;
}

export function useFetchRecommendedCreatives(ticketId: string) {
  const query = useQuery({
    queryKey: ["ticket-recommended-creatives", ticketId],
    queryFn: () => fetchRecommendedCreatives(ticketId),
  });

  return query;
}

export function useFetchRecommendedReviewers(ticketId: string) {
  const query = useQuery({
    queryKey: ["ticket-recommended-reviewers", ticketId],
    queryFn: () => fetchRecommendedReviewers(ticketId),
  });

  return query;
}

export function useGetAndSendUrl() {
  const mutation = useMutation({
    mutationKey: ["send-url"],
    mutationFn: shareTicket,
  });

  return mutation;
}

export function useDeleteTicketMutation() {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["delete-ticket"],
    mutationFn: deleteTicket,
    onSuccess(_data, { ticketId }) {
      dispatch(removeTicket({ ticketId }));
      queryClient.setQueriesData(
        { queryKey: ["folders-and-tickets"] },
        (old: { tickets?: { _id: string }[] } | undefined) => {
          if (old?.tickets == null) return old;
          return {
            ...old,
            tickets: old.tickets.filter((ticket) => ticket._id !== ticketId),
          };
        },
      );
      void queryClient.invalidateQueries({ queryKey: ["folders-and-tickets"] });
    },
  });
}

export function useCreateFolderMutation() {
  const mutation = useMutation({
    mutationKey: ["create-folder"],
    mutationFn: createFolder,
  });

  return mutation;
}

export function useFoldersAndTicketsMutation(folderId?: string) {
  const dispatch = useAppDispatch();
  const folders = useAppSelector((state) => state.punttProjects.folders);
  const tickets = useAppSelector((state) => state.punttProjects.tickets);

  return useMutation({
    mutationFn: (params: Parameters<typeof getFoldersAndTickets>[0]) =>
      getFoldersAndTickets(params, folderId),
    mutationKey: ["folders-and-tickets", folderId],
    onSuccess(data) {
      dispatch(
        mergePunttProjectState({
          folders: [...folders, ...(data.folders ?? [])],
          tickets: [...tickets, ...data.tickets],
          more: data.more,
          isLoadingMore: false,
        }),
      );
    },
  });
}

export function useDeleteFolderMutation() {
  const mutation = useMutation({
    mutationKey: ["delete-folder"],
    mutationFn: deleteFolder,
  });
  return mutation;
}

export function useUpdateFolderMutation() {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationKey: ["update-folder"],
    mutationFn: updateFolder,
    onSuccess(data, variables) {
      // Update the breadcrumb name immediately using the input data
      if (variables.name) {
        dispatch(
          updateBreadcrumbName({
            folderId: variables._id,
            newName: variables.name,
          }),
        );
      }

      // Invalidate relevant queries to trigger re-fetches
      queryClient.invalidateQueries({ queryKey: ["folders-and-tickets"] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
  return mutation;
}

export function useShareTicketLink() {
  return useMutation({
    mutationKey: ["tickets", "share", "link"],
    mutationFn: shareTicket2,
  });
}

export function useShareTicket() {
  return useMutation({
    mutationKey: ["tickets", "share"],
    mutationFn: shareTicket2,
  });
}

export const useCreateAskPunttComment = () => {
  const dispatch = useAppDispatch();
  const comments = useAppSelector((state) => state.ticket.comments);

  return useMutation({
    mutationKey: ["create-ask-puntt-comment"],
    mutationFn: requestAskPunttComment,
    onSuccess(data) {
      if (!isNil(data)) {
        const replaceCommentIndex = comments.findIndex(
          (comment) => comment._id === data._id,
        );
        const commentsCopy = [...comments];
        if (replaceCommentIndex === -1) {
          // The AI comment came back with a server-generated _id that isn't in
          // the current list (the optimistic temp comment used a random id).
          // splice(-1, 1, data) would clobber the LAST existing comment, so
          // append the new comment instead of replacing an unrelated one.
          commentsCopy.push(data);
        } else {
          commentsCopy.splice(replaceCommentIndex, 1, data);
        }

        dispatch(setComments(commentsCopy));
      }
    },
  });
};

export function useDownloadTicketLink() {
  return useMutation({
    mutationKey: ["tickets", "download"],
    mutationFn: getDownloadTicketLink,
  });
}

export const useCombineTickets = () => {
  return useMutation({
    mutationKey: ["combine-tickets"],
    mutationFn: combineTickets,
  });
};

export function useTicketVersionStatusMutation(ticketId: string) {
  return useMutation({
    mutationKey: ["tickets", ticketId, "status"],
    mutationFn(versionId: string) {
      return getTicketVersionStatus({ ticketId, versionId });
    },
  });
}

// Integration response mutations
export function useGenerateIntegrationSummaryMutation() {
  return useMutation({
    mutationKey: ["integration-summary", "generate"],
    mutationFn: generateIntegrationSummary,
  });
}

export function useSubmitIntegrationSummaryMutation() {
  return useMutation({
    mutationKey: ["integration-summary", "submit"],
    mutationFn: submitIntegrationSummary,
  });
}
